import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _sampleSize from "lodash/sampleSize";
import { signIn, signOut, signUp } from 'aws-amplify/auth';
import { createPath } from 'history';
import qs from 'qs';
import * as React from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import { Navigate, useLocation as useLocationV6 } from 'react-router-dom-v5-compat';
import { AppName, createLogger, Sentry, SentryUserType, Tag } from '../logging';
import { ExternalRedirect, ExternalRedirectV6, parseQs, updateQs } from '../routing';
import { dataLayer, GtmEvent } from '../tracking';
import { maskEmail } from '../utils';
import { useAuthenticated } from './context';
import { getAuthFlowType } from './init';
import { sha256 } from './crypto';
import { cognitoError, CognitoSignInErrorCode, getCognitoErrorCode } from './error';
import { jsx as _jsx } from "react/jsx-runtime";
const logger = createLogger(AppName.Common, 'auth');
export const locationWithNext = (location, to) => {
  const {
    pathname: next,
    search,
    state
  } = location;
  const newLocation = typeof to === 'string' ? {
    pathname: to,
    search: qs.stringify({
      ...qs.parse(search, {
        ignoreQueryPrefix: true
      }),
      next
    }),
    state
  } : {
    ...to,
    search: qs.stringify({
      ...qs.parse(to.search || '', {
        ignoreQueryPrefix: true
      }),
      next
    }),
    state
  };
  return newLocation;
};
export const UnauthedRedirect = ({
  to: toProp
}) => {
  const location = useLocation();
  const to = locationWithNext(location, toProp);
  return _jsx(Redirect, {
    to: to
  });
};
const toFromNext = (next, location) => {
  let to;
  let external = false;
  if (typeof next === 'string') {
    const qs = parseQs(next.split('?')[1] || '');
    to = {
      pathname: next.split('?')[0],
      search: updateQs(location.search, {
        next: undefined,
        ...qs
      })
    };
  } else {
    to = {
      pathname: next.pathname,
      search: updateQs(location.search, {
        next: undefined
      })
    };
    external = next.external;
  }
  return [to, external];
};
export const AuthedRedirect = ({
  next,
  defaultNext
}) => {
  const location = useLocation();
  const {
    next: nextQs
  } = parseQs(location.search);
  const resolved = next || nextQs || defaultNext;
  if (resolved) {
    const [to, external] = toFromNext(resolved, location);
    if (external) {
      return _jsx(ExternalRedirect, {
        to: createPath(to)
      });
    }
    return _jsx(Redirect, {
      to: to
    });
  }
  if (process.env.NODE_ENV === 'development') {
    console.warn('No available next option. Fallback to "/".');
  }
  return _jsx(Redirect, {
    to: "/"
  });
};
export const AuthedRedirectV6 = ({
  next,
  defaultNext
}) => {
  const location = useLocationV6();
  const {
    next: nextQs
  } = parseQs(location.search);
  const resolved = next || nextQs || defaultNext;
  if (resolved) {
    const [to, external] = toFromNext(resolved, location);
    if (external) {
      return _jsx(ExternalRedirectV6, {
        to: createPath(to)
      });
    }
    return _jsx(Navigate, {
      to: to,
      replace: true
    });
  }
  if (process.env.NODE_ENV === 'development') {
    console.warn('No available next option. Fallback to "/".');
  }
  return _jsx(Navigate, {
    to: "/",
    replace: true
  });
};
export function switchByAuth(authed, unauthed, displayName) {
  const AuthSwitch = props => {
    const authenticated = useAuthenticated();
    const child = authenticated ? authed : unauthed;
    return React.cloneElement(child, props);
  };
  AuthSwitch.displayName = displayName ? `withAuthSwitch(${displayName})` : 'withAuthSwitch';
  return AuthSwitch;
}
const randomString = (size, collection = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789') => {
  const collectionArr = typeof collection === 'string' ? collection.split('') : collection;
  return _sampleSize(collectionArr, size).join('');
};
export const signUpWithoutVerification = function () {
  var _ref = _asyncToGenerator(function* (form) {
    const {
      email,
      phone_number: phoneNumber,
      token
    } = form;
    const initialPassword = randomString(32);
    yield signUp({
      username: email.toLowerCase(),
      password: initialPassword,
      options: {
        userAttributes: {
          phone_number: phoneNumber,
          'custom:temp_phone_number': phoneNumber
        },
        validationData: {
          recaptchaToken: token,
          verification: 'skip'
        }
      }
    });
    const {
      nextStep
    } = yield signInReplacingSession({
      username: email.toLowerCase(),
      password: initialPassword
    });
    assertSignInComplete(nextStep.signInStep);
  });
  return function signUpWithoutVerification(_x) {
    return _ref.apply(this, arguments);
  };
}();
export const USER_CHANGE_CALLBACK = {
  LOGGER: (user, prevUser) => {
    if (!prevUser && user) {
      logger.log('User signed in');
      Sentry.configureScope(scope => {
        scope.setUser({
          id: user.username,
          email: maskEmail(user.attributes.email),
          ip_address: '{{auto}}'
        });
        scope.setTag(Tag.UserType, SentryUserType.Customer);
      });
    }
    if (prevUser && !user) {
      logger.log('User signed out');
      Sentry.configureScope(scope => {
        scope.setUser(null);
        scope.setTag(Tag.UserType, SentryUserType.Guest);
      });
    }
  },
  TRACKING: function () {
    var _ref2 = _asyncToGenerator(function* (user, prevUser) {
      if (!prevUser && user) {
        dataLayer.push({
          event: GtmEvent.UserLoggedIn,
          user: {
            id: user.username,
            idSha256: yield sha256(user.username)
          }
        });
      }
      if (prevUser && !user) {
        dataLayer.push({
          event: GtmEvent.UserLoggedOut,
          user: {
            id: undefined,
            idSha256: undefined
          }
        });
      }
    });
    return function TRACKING(_x2, _x3) {
      return _ref2.apply(this, arguments);
    };
  }()
};
export const decodeAndJsonParse = base64 => {
  const json = atob(base64);
  return JSON.parse(json);
};
export const isForceResetPassword = user => {
  var _user$attributes;
  return (user === null || user === void 0 ? void 0 : (_user$attributes = user.attributes) === null || _user$attributes === void 0 ? void 0 : _user$attributes['custom:force_reset_password']) === 'True';
};
export const signInReplacingSession = function () {
  var _ref3 = _asyncToGenerator(function* (input) {
    var _input$options$authFl, _input$options;
    const authFlowType = (_input$options$authFl = (_input$options = input.options) === null || _input$options === void 0 ? void 0 : _input$options.authFlowType) !== null && _input$options$authFl !== void 0 ? _input$options$authFl : getAuthFlowType();
    const withFlow = authFlowType ? {
      ...input,
      options: {
        ...input.options,
        authFlowType
      }
    } : input;
    try {
      return yield signIn(withFlow);
    } catch (err) {
      if (getCognitoErrorCode(err) !== CognitoSignInErrorCode.UserAlreadyAuthenticatedException) {
        throw err;
      }
      yield signOut();
      return signIn(withFlow);
    }
  });
  return function signInReplacingSession(_x4) {
    return _ref3.apply(this, arguments);
  };
}();
export const assertSignInComplete = (signInStep, language) => {
  if (signInStep === 'DONE') {
    return;
  }
  const code = signInStep === 'CONFIRM_SIGN_UP' ? CognitoSignInErrorCode.UserNotConfirmedException : signInStep === 'RESET_PASSWORD' ? CognitoSignInErrorCode.PasswordResetRequiredException : signInStep;
  throw cognitoError(code, {
    signInStep
  }, language);
};